import { useState } from "react";
import { motion } from "framer-motion";
import { useLanguage } from "../i18n/LanguageContext";
import { Reveal, SectionLabel } from "./Reveal";

export const Contribute = () => {
  const { t } = useLanguage();
  const w = t.contribute;
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <section id="contribute" data-testid="contribute-section" className="py-24 md:py-60">
      <div className="mx-auto max-w-[1600px] px-6 md:px-12">
        <div className="grid grid-cols-12 gap-4 md:gap-16">
          <div className="col-span-12 md:col-span-5 md:col-start-2">
            <Reveal>
              <SectionLabel>{w.label}</SectionLabel>
            </Reveal>
            <Reveal delay={0.1}>
              <h2
                data-testid="contribute-heading"
                className="mt-10 font-normal text-3xl md:text-4xl tracking-tight leading-[1.15]"
              >
                {w.heading}
              </h2>
            </Reveal>
            <Reveal delay={0.2} className="mt-12 md:mt-14 max-w-xl">
              <p className="text-base md:text-lg leading-relaxed text-foreground/75 tracking-wide">{w.p1}</p>
            </Reveal>
          </div>

          <div className="col-span-8 col-start-3 md:col-span-3 md:col-start-9 mt-4 md:mt-24">
            <Reveal delay={0.25}>
              <figure data-testid="contribute-photo">
                <div className="overflow-hidden">
                  <img
                    src="/media/studio-visit.webp"
                    alt={t.altStudioVisit}
                    className="w-full aspect-[4/5] object-cover"
                  />
                </div>
                <figcaption className="mt-3 text-[11px] tracking-[0.12em] text-foreground/45">
                  {t.capStudioVisit}
                </figcaption>
              </figure>
            </Reveal>
          </div>
        </div>

        <div className="mt-24 md:mt-36">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] text-foreground/50 md:ml-[16.66%]">
              {w.situationsLabel}
            </p>
          </Reveal>
          <div className="mt-10 md:ml-[16.66%]">
            {/* Mobile accordion */}
            <div className="md:hidden" data-testid="situations-accordion">
              {w.situations.map((s, i) => {
                const open = openIndex === i;
                return (
                  <div key={i} className="border-t border-foreground/15 last:border-b">
                    <button
                      type="button"
                      data-testid={`situation-accordion-button-${i + 1}`}
                      aria-expanded={open}
                      aria-controls={`situation-panel-${i + 1}`}
                      onClick={() => setOpenIndex(open ? null : i)}
                      className="w-full flex items-center justify-between gap-6 py-5 text-left font-medium text-lg tracking-tight"
                    >
                      <span>{w.situationQuestions[i] || s.q}</span>
                      <span aria-hidden="true" className="text-foreground/50 font-light text-xl leading-none">
                        {open ? "−" : "+"}
                      </span>
                    </button>
                    <motion.div
                      id={`situation-panel-${i + 1}`}
                      data-testid={`situation-accordion-panel-${i + 1}`}
                      initial={false}
                      animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }}
                      transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                      className="overflow-hidden"
                    >
                      <p className="pb-6 pr-8 text-base leading-relaxed text-foreground/70 tracking-wide">
                        {s.a}
                      </p>
                    </motion.div>
                  </div>
                );
              })}
            </div>

            {/* Desktop list */}
            <div className="hidden md:block">
              {w.situations.map((s, i) => (
                <Reveal key={i} delay={0.04 * i}>
                  <div
                    data-testid={`situation-${i + 1}`}
                    className="group border-t border-foreground/15 last:border-b py-8 md:py-10 grid grid-cols-12 gap-3 md:gap-10 items-baseline"
                  >
                    <h3 className="col-span-12 md:col-span-4 font-medium text-lg md:text-xl tracking-tight transition-transform duration-500 group-hover:translate-x-2">
                      {s.q}
                    </h3>
                    <p className="col-span-12 md:col-span-7 text-base leading-relaxed text-foreground/70 tracking-wide">
                      {s.a}
                    </p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
